// filepath: apps/web/src/features/tasks/TaskRow.tsx
//
// Single task row for the task inbox (FR-UI-TASK-1).
//
// Renders:
//   - Task title, process name, form name
//   - Due date (or overdue badge via DueDateBadge)
//   - Action buttons: Claim (unclaimed) / Complete (claimed)
//   - Delegate button [S] (conditionally, behind a feature flag for v1)
//
// AC-1: "My Tasks" tab row
// AC-2: "Unclaimed (my roles)" tab row
// AC-3: Claim removes from unclaimed (handled by the parent via TanStack Query)
// AC-6: overdue badge via DueDateBadge

import React from "react";
import { DueDateBadge } from "@/renderers/task/DueDateBadge";
import { TaskClaim } from "@/renderers/task/TaskClaim";
import type { HumanTask } from "./types";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export interface TaskRowProps {
  /** The task to render. */
  task: HumanTask;
  /** Called when the user clicks Claim on an unclaimed task. */
  onClaimed?: (taskId: string) => void;
  /** Called when the user clicks Complete on a claimed task. */
  onComplete?: (task: HumanTask) => void;
  /** Called when the user clicks Delegate [S] on a claimed task. */
  onDelegate?: (task: HumanTask) => void;
  /** Whether to show the Claim button (for unclaimed tasks in the unclaimed tab). */
  showClaimButton?: boolean;
  /** Whether to show the Complete button (for claimed tasks in My Tasks). */
  showCompleteButton?: boolean;
}

/**
 * TaskRow — a single row in the task inbox.
 *
 * Visual layout:
 *   [TITLE]  [PROCESS NAME]  [FORM NAME]  [DUE DATE + BADGE]  [ACTIONS]
 *
 * The parent (TaskInbox) is responsible for filtering which tasks
 * get which action buttons based on the active tab.
 */
export function TaskRow({
  task,
  onClaimed,
  onComplete,
  onDelegate,
  showClaimButton = false,
  showCompleteButton = false,
}: TaskRowProps): React.ReactElement {
  const isMobile = useIsMobile();

  // FR-UI-NFR-3 AC-1: Mobile card layout at 375px.
  if (isMobile) {
    return (
      <div
        data-testid="task-row"
        data-task-id={task.id}
        data-task-status={task.status}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "transparent",
        }}
      >
        {/* Title + process name */}
        <div>
          <div
            data-testid="task-title"
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: "#111827",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {task.title}
          </div>
          <div
            data-testid="task-process-name"
            style={{
              fontSize: "12px",
              color: "#6b7280",
              marginTop: "2px",
            }}
          >
            {task.process_name}
          </div>
        </div>

        {/* Form name */}
        <div
          data-testid="task-form-name"
          style={{
            fontSize: "13px",
            color: "#374151",
          }}
        >
          {task.form_id}
        </div>

        {/* Due date + overdue badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {task.due_at && (
            <DueDateBadge dueAt={task.due_at} status={task.status} />
          )}
          {!task.due_at && (
            <span
              data-testid="task-no-due-date"
              style={{ fontSize: "12px", color: "#9ca3af" }}
            >
              No due date
            </span>
          )}
        </div>

        {/* Action buttons — stacked, full-width on mobile */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {showClaimButton && task.status === "unclaimed" && (
            <TaskClaim taskId={task.id} onClaimed={onClaimed} isMobile />
          )}

          {showCompleteButton && task.status === "claimed" && (
            <button
              data-testid="task-complete-button"
              type="button"
              onClick={() => onComplete?.(task)}
              style={{
                width: "100%",
                minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid #059669",
                backgroundColor: "#059669",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              Complete
            </button>
          )}

          {showCompleteButton && task.status === "claimed" && (
            <button
              data-testid="task-delegate-button"
              type="button"
              onClick={() => onDelegate?.(task)}
              style={{
                width: "100%",
                minHeight: `${MIN_TOUCH_TARGET_PX}px`,
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#374151",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              Delegate
            </button>
          )}
        </div>
      </div>
    );
  }

  // Desktop row layout
  return (
    <div
      data-testid="task-row"
      data-task-id={task.id}
      data-task-status={task.status}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 16px",
        borderBottom: "1px solid #e5e7eb",
        backgroundColor: "transparent",
      }}
    >
      {/* Title + process name */}
      <div style={{ flex: "1 1 200px", minWidth: 0 }}>
        <div
          data-testid="task-title"
          style={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#111827",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={task.title}
        >
          {task.title}
        </div>
        <div
          data-testid="task-process-name"
          style={{
            fontSize: "12px",
            color: "#6b7280",
            marginTop: "2px",
          }}
        >
          {task.process_name}
        </div>
      </div>

      {/* Form name */}
      <div
        data-testid="task-form-name"
        style={{
          flex: "0 0 120px",
          fontSize: "13px",
          color: "#374151",
        }}
      >
        {task.form_id}
      </div>

      {/* Due date + overdue badge */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {task.due_at && (
          <DueDateBadge dueAt={task.due_at} status={task.status} />
        )}
        {!task.due_at && (
          <span
            data-testid="task-no-due-date"
            style={{ fontSize: "12px", color: "#9ca3af" }}
          >
            No due date
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ flex: "0 0 auto", display: "flex", gap: "8px" }}>
        {showClaimButton && task.status === "unclaimed" && (
          <TaskClaim taskId={task.id} onClaimed={onClaimed} />
        )}

        {showCompleteButton && task.status === "claimed" && (
          <button
            data-testid="task-complete-button"
            type="button"
            onClick={() => onComplete?.(task)}
            style={{
              padding: "4px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid #059669",
              backgroundColor: "#059669",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Complete
          </button>
        )}

        {showCompleteButton && task.status === "claimed" && (
          <button
            data-testid="task-delegate-button"
            type="button"
            onClick={() => onDelegate?.(task)}
            style={{
              padding: "4px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Delegate
          </button>
        )}
      </div>
    </div>
  );
}
